import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useColors } from 'vuestic-ui'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'vetcrecon.theme-mode'
const mode = ref<ThemeMode>('system')
const initialized = ref(false)

function systemIsDark() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
}

function readMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  const value = window.localStorage.getItem(STORAGE_KEY)
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system'
}

export function useThemeMode() {
  const { applyPreset } = useColors()
  const effectiveMode = computed(() => (mode.value === 'system' ? (systemIsDark() ? 'dark' : 'light') : mode.value))

  function apply() {
    applyPreset(effectiveMode.value)
    document.documentElement.dataset.theme = effectiveMode.value
    document.documentElement.style.colorScheme = effectiveMode.value
  }

  function setMode(value: ThemeMode) {
    mode.value = value
    window.localStorage.setItem(STORAGE_KEY, value)
    apply()
  }

  function onSystemChange() {
    if (mode.value === 'system') apply()
  }

  if (!initialized.value && typeof window !== 'undefined') {
    mode.value = readMode()
    initialized.value = true
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener?.('change', onSystemChange)
    watch(mode, apply)
    apply()
    onBeforeUnmount(() => media.removeEventListener?.('change', onSystemChange))
  }

  return { mode, effectiveMode, setMode }
}
